/**
 * Audio Player Component
 * Compact audio player for voice messages
 */

import { useState, useRef, useEffect } from 'react';
import { tokenManager } from '@/services/api.client';

interface AudioPlayerProps {
  src: string;
  fileName?: string;
}

const WAVEFORM_BUCKETS = 40;

/**
 * Format seconds to MM:SS
 * Handles NaN and Infinity cases gracefully
 */
const formatTime = (seconds: number): string => {
  // Handle invalid durations
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
    return '--:--';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Downsample a decoded audio buffer into N normalized (0-1) RMS peaks for the
 * waveform bars. Returns null if there's no usable signal.
 */
const computePeaks = (buffer: AudioBuffer, buckets = WAVEFORM_BUCKETS): number[] | null => {
  const data = buffer.getChannelData(0);
  if (!data.length) return null;
  const blockSize = Math.floor(data.length / buckets) || 1;
  const peaks: number[] = [];
  let max = 0;
  for (let i = 0; i < buckets; i++) {
    let sum = 0;
    const start = i * blockSize;
    for (let j = 0; j < blockSize; j++) {
      const v = data[start + j] || 0;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / blockSize);
    peaks.push(rms);
    if (rms > max) max = rms;
  }
  return max > 0 ? peaks.map((p) => p / max) : null;
};

export const AudioPlayer = ({ src }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  // Waveform peaks (null until computed, or on decode failure → slider fallback)
  const [peaks, setPeaks] = useState<number[] | null>(null);

  // Fetch audio data and create a blob URL for reliable playback
  useEffect(() => {
    let revoked = false;
    let blobUrl: string | null = null;

    const fetchAudioData = async () => {
      try {
        const token = tokenManager.getAccessToken();
        // Build absolute URL: src may be a relative path like "/api/attachments/…/download"
        // but the API lives on a different subdomain in production.
        const apiBase = import.meta.env.VITE_API_URL;
        const url = apiBase && src.startsWith('/api/')
          ? apiBase + src.replace(/^\/api/, '')
          : src;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          redirect: 'follow',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }

        const blob = await response.blob();
        if (revoked) return;

        blobUrl = URL.createObjectURL(blob);
        setAudioUrl(blobUrl);

        // Decode the same blob for the waveform. Best-effort: if the codec can't
        // be decoded, we fall back to the slider scrubber (no dead UI).
        try {
          const arrayBuf = await blob.arrayBuffer();
          const AudioCtx =
            window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
          const ctx = new AudioCtx();
          const decoded = await ctx.decodeAudioData(arrayBuf);
          ctx.close();
          if (!revoked) setPeaks(computePeaks(decoded));
        } catch {
          if (!revoked) setPeaks(null);
        }
      } catch (err) {
        console.error('[AudioPlayer] Failed to load audio:', err);
        if (!revoked) setError('Failed to load audio');
      }
    };

    fetchAudioData();

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  // Track the highest currentTime seen — used as duration fallback for WebM
  // files where audio.duration stays Infinity (missing container metadata)
  const maxTimeRef = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    maxTimeRef.current = 0;

    const isValidDuration = (d: number) => isFinite(d) && !isNaN(d) && d > 0;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Track max time for duration fallback
      if (audio.currentTime > maxTimeRef.current) {
        maxTimeRef.current = audio.currentTime;
      }
      // Duration can become available during playback
      if (isValidDuration(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleDurationChange = () => {
      if (isValidDuration(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // If duration was never resolved, use the max time we saw
      if (!isValidDuration(audio.duration) && maxTimeRef.current > 0) {
        setDuration(maxTimeRef.current);
      }
      setCurrentTime(0);
    };

    const handleError = (e: Event) => {
      console.error('Audio playback error:', e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Seek to a 0-1 fraction of the track (used by the waveform click).
  const seekToFraction = (fraction: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur =
      duration > 0
        ? duration
        : isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : maxTimeRef.current;
    if (!dur || dur <= 0) return;
    const newTime = Math.max(0, Math.min(1, fraction)) * dur;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    seekToFraction((e.clientX - rect.left) / rect.width);
  };

  const togglePlaybackRate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];

    audio.playbackRate = nextRate;
    setPlaybackRate(nextRate);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Show loading state while fetching audio URL
  if (!audioUrl && !error) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2 max-w-sm">
        <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center space-x-1">
          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          <span>Зареждане...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 space-y-2 max-w-sm">
        <div className="text-xs text-red-600 dark:text-red-400 flex items-center space-x-1">
          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Грешка при зареждане на аудио</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2 max-w-sm">
      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl ?? undefined} preload="auto" />

      {/* Voice message indicator (no filename displayed) */}
      <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center space-x-1">
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
        <span>Гласово съобщение</span>
      </div>

      {/* Main player row */}
      <div className="flex items-center space-x-2">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white rounded-full transition-colors"
          title={isPlaying ? 'Пауза' : 'Пусни'}
        >
          {isPlaying ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Scrubber: bar waveform when decoded, themed slider fallback otherwise */}
        <div className="flex-1 flex items-center">
          {peaks && peaks.length > 0 ? (
            <div
              className="flex-1 flex items-center gap-[2px] h-6 cursor-pointer"
              onClick={handleWaveformClick}
              role="slider"
              aria-label="Позиция на възпроизвеждане"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
            >
              {peaks.map((p, i) => {
                const filled = (i + 0.5) / peaks.length <= progress / 100;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-full transition-colors ${
                      filled ? 'bg-blue-500 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    style={{ height: `${Math.max(10, p * 100)}%` }}
                  />
                );
              })}
            </div>
          ) : (
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="flex-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progress}%, rgba(156,163,175,0.4) ${progress}%, rgba(156,163,175,0.4) 100%)`,
              }}
            />
          )}
        </div>

        {/* Time Display */}
        <div className="text-xs text-gray-600 dark:text-gray-400 font-mono min-w-[60px] text-right">
          {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : '--:--'}
        </div>

        {/* Playback Speed */}
        <button
          onClick={togglePlaybackRate}
          className="flex-shrink-0 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Скорост на възпроизвеждане"
        >
          {playbackRate}x
        </button>

        {/* Volume Control */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            title="Сила на звука"
          >
            {volume === 0 ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : volume < 0.5 ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>

          {/* Volume Slider Popup */}
          {showVolumeSlider && (
            <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="h-20 w-1 appearance-none bg-gray-300 dark:bg-gray-600 rounded-lg cursor-pointer"
                style={{
                  writingMode: 'vertical-lr' as React.CSSProperties['writingMode'],
                  WebkitAppearance: 'slider-vertical',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
